import { Injectable, OnDestroy, Optional } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import {
  Auth, User, GoogleAuthProvider,
  authState, createUserWithEmailAndPassword,
  deleteUser, sendEmailVerification, signInAnonymously,
  signInWithEmailAndPassword, signInWithPopup, signOut, sendPasswordResetEmail,
} from '@angular/fire/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { nav_path } from 'src/app/app-routing.module';
import { ConsoleLoggerService } from './console-logger.service';
import { Functions, httpsCallableData } from '@angular/fire/functions';
import { UpdateProfileResponse } from '../../shared/interfaces/functions-response';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private unsub$: Subject<any> = new Subject<any>();
  user$: Observable<User|null> = of(null);
  user: User | null = null;
  userClaims: any;

  constructor(
    @Optional() private auth: Auth,
    private router: Router,
    private cLog: ConsoleLoggerService,
    private functions: Functions,
    private route: ActivatedRoute
  ) {
    /* Check if user is signed in */
    authState(auth).pipe(takeUntil(this.unsub$)).subscribe(user => {
      /* if signed in, store to reference for rest of webapp */
      if (user != null) {
        /* store user custom claims */
        user.getIdTokenResult().then(idTokenResult => this.userClaims = idTokenResult?.claims);
        this.user = user!;
        this.user$ = of(user);
      } else {
        this.user$ = of(null);
        this.user = null;
        this.userClaims = null;
      }
    });
  }

  /*
  * check if signed-in user is an admin
  */
  isAdmin(): boolean {
    if (this.userClaims != null) { return this.userClaims.admin === true; }
    return false
  }

  /*
  * sign user in anonymously
  */
  async loginAnonymously() {
    await signInAnonymously(this.auth)
      .then(userCredential => this.routeRedirect(userCredential.user))
      .catch(error => this.handleAuthError(``, error, this.auth));
  }

  /*
  * sign user in with Google
  */
  async googleLogin() {
    const provider = new GoogleAuthProvider();
    return await signInWithPopup(this.auth, provider)
      .then(newCredential => {
        this.routeRedirect(newCredential.user!);
      })
      .catch(error => this.cLog.error(`error: ${error.code}`, error, this.auth));
  }

  /*
  * sign user out
  */
  async logout() {
    await signOut(this.auth)
      .then(() => {
        this.user$ = of(null);
        this.cLog.info(`Successfully signed out`);
        this.router.navigate([nav_path.home]);
      })
      .catch(error => this.handleAuthError(`error: ${error.code}`, error, this.auth));
  }

  /*
  * delete and logout signed-in user account from web app
  */
  delete(user: User) {
    return deleteUser(user)
      .then(() => this.cLog.info(`User deleted, removing user content in progress!`))
      .then(() => this.router.navigate([nav_path.home]))
      .catch((error: any) => this.handleAuthError(`error deleting user: '${user.email}', refresh and try again`, error, user));
  }

  /*
  * sign user up with provided credentials
  */
  signUp(email: string, password: string) {
    return createUserWithEmailAndPassword(this.auth, email, password)
      .then((userCredential) => {
        // this.updateUserData(userCredential.user!);
        this.sendVerificationEmail(userCredential.user!);
        this.routeRedirect(userCredential.user!);
      })
      .catch(error => this.cLog.error(`error creating user: '${email}'`, [email, password], error));
  }

  /*
  * sign user in with provided credentials
  */
  signIn(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password)
      .then((userCredential) => {
        this.routeRedirect(userCredential.user!);
      } )
      .catch(error => this.handleAuthError(`error signing in: '${email}'`, error, [email, password]));
  }

  /*
  * send verification email to provided user
  */
  sendVerificationEmail(user: User) { return sendEmailVerification(user)
    .catch(error => this.handleAuthError(`error sending email verification to '${user?.email ?? 'unknown email'}'`, error, user)); }

  /*
  * send password reset email to provided email address
  */
  sendPasswordResetEmail(email: string) {
    return sendPasswordResetEmail(this.auth, email)
      .then(() => 'success')
      .catch(error => this.handleAuthError(`${email}: ${error.message}`, error, email));
  }

  /*
  * update user with provided user information
  */
  updateUser(updateUserData: {
    displayName?: string | null;
    photoURL?: string | null;
  }): Promise<UpdateProfileResponse> {
    return httpsCallableData(this.functions, 'user-update')(updateUserData)
      .toPromise() as Promise<UpdateProfileResponse>;
  }

  /*
  * redirect user to previous url attempted or to home
  */
  routeRedirect(user: User) {
    if (user && user.uid) {
      const routeParams = this.route.snapshot.queryParams;
      let route = routeParams['redirectURL'] ?? nav_path.home;
      this.router.navigate([route]);
    }
  }

  /*
  * handle authentication errors
  */
  handleAuthError(message: string = '', error: any, ...args: any) {
    let _message = '';
    if (message) _message = message;
    else if (!message && error.code) {
      _message = `error: ${error.code}`;
    }
    else if (!message && error.message) {
      _message = `error: ${error.message}`;
    }
    else {
      _message = `unknown error occurred`;
    }
    this.cLog.error(_message, error, ...args);
  }

  /*
  * grant signed-in user admin role
  */
  async grantAdminRole() {
    return httpsCallableData(this.functions, 'admin-updateClaims')({"admin": true})
      .toPromise().then(() => {
        this.cLog.openSnackBar(
          `Granted admin role, re-login for it take effect.`,
          `Logout`,
          {duration: 8000}
        ).onAction().subscribe(() => this.auth.signOut()
          .then(() => this.router.navigate([nav_path.home])))
      });
  }
  /*
  * revoke signed-in user admin role
  */
  async revokeAdminRole() {
    return httpsCallableData(this.functions, 'admin-updateClaims')({"admin": false})
      .toPromise()
      .then(() => {
        this.cLog.openSnackBar(
          `Removed admin role, re-login for it take effect.`,
          `Logout`,
          {duration: 8000}
        ).onAction().subscribe(() => this.auth.signOut()
          .then(() => this.router.navigate([nav_path.home])))
      });
  }

  ngOnDestroy() { this.unsub$.next(undefined); this.unsub$.complete(); }
}
